import React, { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import PitchCodeBadge from './PitchCodeBadge';

const CountDots = ({ balls, strikes }) => (
  <div className="flex flex-col gap-0.5 shrink-0">
    <div className="flex gap-0.5">
      {[0, 1, 2].map(i => (
        <div key={i} className={`w-2 h-2 rounded-full border ${i < strikes ? 'bg-[#c4333e] border-[#c4333e]' : 'border-[#658d71] bg-transparent'}`} />
      ))}
    </div>
    <div className="flex gap-0.5">
      {[0, 1, 2, 3].map(i => (
        <div key={i} className={`w-2 h-2 rounded-full border ${i < balls ? 'bg-[#386646] border-[#a9c9b1]' : 'border-[#658d71] bg-transparent'}`} />
      ))}
    </div>
  </div>
);

const PitchRow = ({ pitch, index }) => (
  <motion.div
    initial={{ opacity: 0, x: -8 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.15, delay: index * 0.02 }}
    className={`flex items-center gap-2 p-2 border-b border-[rgba(101,141,113,0.2)] last:border-0 ${
      index === 0 ? 'bg-[rgba(216,206,179,0.04)]' : ''
    }`}
  >
    <div className="shrink-0 flex flex-col items-center w-8">
      <span className="text-[7px] font-bold text-[#658d71] uppercase tracking-widest">
        {pitch.isTop ? '▲' : '▼'}{pitch.inning}
      </span>
    </div>

    <PitchCodeBadge code={pitch.code} />

    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-[9px] font-black text-white uppercase truncate max-w-[60px]">
          {pitch.batter.split(' ').pop()}
        </span>
        <span className="text-[8px] text-[#658d71]">vs</span>
        <span className="text-[9px] font-bold text-[#a9c9b1] uppercase truncate max-w-[60px]">
          {pitch.pitcher.split(' ').pop()}
        </span>
      </div>
      <p className="text-[8px] text-[#658d71] leading-tight mt-0.5 truncate">{pitch.description}</p>
    </div>

    <div className="flex items-center gap-2 shrink-0">
      {pitch.speed && (
        <span className="text-[9px] font-black text-[#d8ceb3] tabular-nums">{pitch.speed}</span>
      )}
      {pitch.count && (
        <CountDots balls={pitch.count.balls} strikes={pitch.count.strikes} />
      )}
    </div>
  </motion.div>
);

const PitchByPitch = ({ pitches, isLive }) => {
  if (!pitches.length) return (
    <div className="flex items-center justify-center py-10 text-[#658d71] text-xs uppercase tracking-widest">
      No pitch data yet
    </div>
  );

  const balls = pitches.filter(p => p.isBall).length;
  const strikes = pitches.filter(p => p.isStrike).length;
  const inPlay = pitches.filter(p => p.isHit).length;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2">
        {[
          { label: 'Pitches', value: pitches.length, color: 'text-white' },
          { label: 'Strikes', value: strikes, color: 'text-[#c4333e]' },
          { label: 'Balls', value: balls, color: 'text-[#a9c9b1]' },
          { label: 'In Play', value: inPlay, color: 'text-[#d8ceb3]' },
        ].map(s => (
          <div key={s.label} className="flex-1 border border-[#658d71] bg-[rgba(0,0,0,0.1)] p-2 flex flex-col items-center">
            <span className={`text-base font-black tabular-nums ${s.color}`}>{s.value}</span>
            <span className="text-[7px] uppercase tracking-widest text-[#658d71] font-bold mt-0.5">{s.label}</span>
          </div>
        ))}
      </div>

      {isLive && (
        <div className="flex items-center gap-2 text-[9px] text-[#c4333e] font-black uppercase tracking-widest">
          <span className="w-1.5 h-1.5 rounded-full bg-[#c4333e] animate-pulse" />
          Live · Most recent first
        </div>
      )}

      <div className="max-h-[420px] overflow-y-auto border border-[#658d71] bg-[rgba(0,0,0,0.08)]">
        <AnimatePresence initial={false}>
          {pitches.map((pitch, i) => (
            <PitchRow key={`${pitch.playIdx}-${i}`} pitch={pitch} index={i} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PitchByPitch;